import {faClockRotateLeft, faClose, faDownload} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Text} from '@rdcomponents/text/text';
import {AutoComplete, AutoCompleteProps, Button, Col, Form, FormInstance, Row, Select} from 'antd';
import {useCallback, useEffect, useState} from 'react';

import {useLicenses} from '../../../../../../provider/license/license-hook';
import {Locale} from '../../../../../../provider/locale/locale';
import {useLocale} from '../../../../../../provider/locale/locale-hook';
import {AnalyticsTarget, track} from '../../../../../../service/analytics/analytics';
import {
    ReviewConcordanceRequestFieldEnum,
    ReviewConcordanceRequestType,
} from '../../../../../../service/reivew-analysis/review-concordance/review-concordance-type';
import {FeaturesEnum} from '../../../../../../service/user/user-type';
import {getFromLocalStorage, saveToLocalStorage} from '../../../../../../util/local-storage';
import {ActiveFilter} from '../../../components/active-filter/active-filter';
import {DrawerFilter} from '../../../components/drawer-filter/drawer-filter';
import {UtcRangePicker} from '../../../components/drawer-filter/utc-range-picker/utc-range-picker';

import {CONCORDANCE_QUERIES_LOCALSTORAGE_KEY} from './concordance-form-const';
import {contextOptionElements} from './concordance-form-helper';
import {autoCompleteOptionsZodSchema, ConcordanceFormType} from './concordance-form-type';
import * as styles from './concordance-form.scss';

type PropsType = {
    values: ConcordanceFormType;
    isExportLoading: boolean;
    onSubmit: (values: ConcordanceFormType) => void;
    getReport: VoidFunction;
};

export function ConcordanceForm(props: PropsType): JSX.Element {
    const {values, onSubmit, getReport, isExportLoading} = props;

    const getReportWrapped = useCallback(() => {
        track(AnalyticsTarget.ReviewsAnalysis.RequestReport, 'xlsx');
        getReport();
    }, [getReport]);

    const {getLocalizedString} = useLocale();

    const [options, setOptions] = useState<AutoCompleteProps['options']>([]);
    const [hoveredOption, setHoveredOption] = useState<string | null>(null);

    const [form]: [FormInstance<ConcordanceFormType>] = Form.useForm();

    const {licenses} = useLicenses();

    useEffect(() => {
        const savedOptions = getFromLocalStorage(
            CONCORDANCE_QUERIES_LOCALSTORAGE_KEY,
            autoCompleteOptionsZodSchema,
            []
        );

        if (savedOptions) {
            setOptions(savedOptions);
        }
    }, []);

    useEffect(() => {
        form.resetFields();
    }, [form, values]);

    const moveToFirst = useCallback(
        (query: string) => {
            const updatedOptions = [{value: query}, ...(options?.filter((option) => option.value !== query) || [])];

            setOptions(updatedOptions);
            saveToLocalStorage(updatedOptions, CONCORDANCE_QUERIES_LOCALSTORAGE_KEY);
        },
        [options]
    );

    const onSubmitWrapper = useCallback(
        (valuesInner: ConcordanceFormType): void => {
            const newQuery = valuesInner.q;

            if (newQuery) {
                moveToFirst(newQuery);
            }

            onSubmit(valuesInner);
            track(AnalyticsTarget.ReviewsAnalysis.SearchByReviews.SearchByReviews, valuesInner.q);
        },
        [onSubmit, moveToFirst]
    );

    return (
        <div>
            <Form<ConcordanceFormType> form={form} initialValues={values} layout="vertical" onFinish={onSubmitWrapper}>
                <div className={styles.ConcordanceForm_filters}>
                    <Form.Item
                        className={styles.ConcordanceForm_range}
                        label={<Locale stringKey="REVIEWS_ANALYSIS__FORM__INPUT__PERIOD__LABEL" />}
                        name={ReviewConcordanceRequestFieldEnum.Range}
                    >
                        <UtcRangePicker />
                    </Form.Item>
                    <div className={styles.ConcordanceForm_downloadAndDrawerContainer}>
                        <Button
                            className={styles.ConcordanceForm_downloadButton}
                            disabled={isExportLoading || !values[ReviewConcordanceRequestFieldEnum.Query]}
                            icon={<FontAwesomeIcon icon={faDownload} />}
                            onClick={getReportWrapped}
                            target="_blank"
                        >
                            <Locale stringKey="REVIEWS_ANALYSIS__RATINGS_DYNAMICS__REPORT__DOWNLOAD_REPORT" />
                        </Button>

                        <DrawerFilter
                            isCompetitorAllowed={licenses[FeaturesEnum.reviewAnalyticsCompetitor]?.isActive}
                            isWithTextOnly
                        />
                    </div>
                </div>

                <div className={styles.ConcordanceForm_activeFilters}>
                    <ActiveFilter
                        isCompareRangeAllowed={false}
                        isCompetitorAllowed
                        isPeriodHidden
                        isPreview={false}
                        isWithTextOnly
                    />
                </div>

                <Row align="bottom" gutter={16}>
                    <Col flex="auto">
                        <Form.Item<ReviewConcordanceRequestType>
                            colon={false}
                            label={getLocalizedString('REVIEWS_ANALYSIS__CONCORDANCE_FORM__INPUT__QUERY__TITLE')}
                            name={ReviewConcordanceRequestFieldEnum.Query}
                            rules={[
                                {required: true, message: <Locale stringKey="VALIDATION__ERROR__FIELD_IS_REQUIRED" />},
                            ]}
                        >
                            <AutoComplete
                                filterOption={(inputValue, option) =>
                                    option?.value?.toString().toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                                }
                                onSelect={(value) => moveToFirst(value)}
                                // eslint-disable-next-line react/no-unstable-nested-components
                                optionRender={(option) => {
                                    return (
                                        <span
                                            className={styles.ConcordanceForm_autoCompeleteOption}
                                            onMouseEnter={() => setHoveredOption(option.value as string)}
                                            onMouseLeave={() => setHoveredOption(null)}
                                        >
                                            <div className={styles.ConcordanceForm_autoCompeleteOption_leftSide}>
                                                <FontAwesomeIcon
                                                    className={styles.ConcordanceForm_autoCompeleteOption_icon}
                                                    icon={faClockRotateLeft}
                                                />
                                                <Text>{option?.value}</Text>
                                            </div>
                                            {hoveredOption === option.value && (
                                                <Button
                                                    className={styles.ConcordanceForm_deleteButton}
                                                    icon={<FontAwesomeIcon icon={faClose} />}
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        const updatedOptions =
                                                            options?.filter((item) => item.value !== option.value) ||
                                                            [];

                                                        setOptions(updatedOptions);
                                                        saveToLocalStorage(
                                                            updatedOptions,
                                                            CONCORDANCE_QUERIES_LOCALSTORAGE_KEY
                                                        );
                                                    }}
                                                    size="small"
                                                    type="text"
                                                />
                                            )}
                                        </span>
                                    );
                                }}
                                options={options}
                                placeholder={getLocalizedString(
                                    'REVIEWS_ANALYSIS__CONCORDANCE_FORM__INPUT__QUERY__PLACEHOLDER'
                                )}
                            />
                        </Form.Item>
                    </Col>

                    <Col>
                        <Form.Item<ReviewConcordanceRequestType>
                            colon={false}
                            label={getLocalizedString('REVIEWS_ANALYSIS__CONCORDANCE_FORM__INPUT__CONTEXT__TITLE')}
                            name={ReviewConcordanceRequestFieldEnum.ContextSize}
                        >
                            <Select optionFilterProp="children">{contextOptionElements}</Select>
                        </Form.Item>
                    </Col>

                    <Col>
                        <Form.Item>
                            <Button htmlType="submit" type="primary">
                                <Locale stringKey="REVIEWS_ANALYSIS__CONCORDANCE_FORM__INPUT__SUBMIT__TITLE" />
                            </Button>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </div>
    );
}
